import { CartItem as CartItemType } from '@/store/cartStore';
import { generateImagePath } from '@/utils/generate-image-path';
import { cn } from '@/utils/tailwind-merge';
import { useLocale, useTranslations } from 'next-intl';
import Image from '../Image';
import { ScreeningType } from './Screening';

const CartItem = ({
    item,
    increaseQuantity,
    decreaseQuantity,
    removeItem,
}: {
    item: CartItemType;
    increaseQuantity: () => void;
    decreaseQuantity: () => void;
    removeItem: () => void;
}) => {
    const t = useTranslations();
    const locale = useLocale();

    let imageSrc = '/img/pass-placeholder.svg';
    let imageAlt = 'Pass Placeholder';
    let title = item.ticket.name?.[locale as keyof typeof item.ticket.name] || '';
    let description =
        item.ticket.description?.[locale as keyof typeof item.ticket.description] || '';
    let screeningType = ScreeningType[item.screening?.screening_type as keyof typeof ScreeningType];

    if (item.film) {
        imageSrc = item.film.poster_url;
        const isPartOfShortProgramme = !!item.film.short_programme;
        if (isPartOfShortProgramme) {
            imageAlt = item.film?.short_programme?.name || '';
            title = item.film?.short_programme?.name || '';
            description = item.film?.short_programme?.description || '';
        } else {
            imageAlt = item.film.title;
            title = item.film.title;
            description = item.film.synopsis;
        }
    }

    const imagePath = imageSrc.startsWith('/img/') ? imageSrc : generateImagePath(imageSrc);

    return (
        <div className="flex max-lg:flex-col gap-5 border-b border-white/50 pb-5">
            <div className="relative min-w-80 z-[1]">
                <div
                    className="w-full h-full absolute inset-0 -z-10"
                    style={{
                        backgroundImage: `url(${imagePath})`,
                        backgroundSize: 'contain',
                        backgroundPosition: '0 -20px',
                        backgroundRepeat: 'repeat-x',
                        filter: 'blur(10px)',
                    }}
                ></div>
                <Image
                    className="hidden sm:block max-h-64 object-contain"
                    src={imagePath}
                    alt={imageAlt}
                    width={318}
                    height={187}
                    forceSrc={!imageSrc.startsWith('/img/')}
                />
                <Image
                    className="sm:hidden object-contain w-full"
                    src={imagePath}
                    alt={imageAlt}
                    width={180}
                    height={80}
                    forceSrc={!imageSrc.startsWith('/img/')}
                />
            </div>
            <div className="w-full flex flex-col lg:gap-6 gap-5">
                <h5 className="text-[22px] lg:text-[28px] uppercase line-clamp-2 text-white">
                    {title}
                </h5>
                <span className="font-helvetica-neue font-light text-p max-lg:text-[12px] text-gray-200 w-full uppercase lg:w-5/6 line-clamp-2">
                    {description}
                    <strong className="font-light normal-case">{}</strong>
                </span>
                <div className="w-full flex justify-between items-center gap-2.5">
                    <span className="uppercase text-gray-200 text-sm md:text-[25px]">
                        [{screeningType ? t(`Cart.${screeningType}`) + ' ' : ''}
                        {item.ticket.name?.[locale as keyof typeof item.ticket.name]}]
                    </span>
                    <div className="min-w-20">
                        <button
                            className={cn('text-md font-helvetica-neue')}
                            onClick={item.quantity === 1 ? removeItem : decreaseQuantity}
                        >
                            -
                        </button>
                        <span className="font-helvetica-neue font-light text-p border border-primary p-1 md:p-3 min-w-9 md:h-9 inline-flex justify-center items-center mx-2 md:mx-5 truncate max-w-9 select-none text-white">
                            {item.quantity}
                        </span>
                        <button
                            className={`text-md font-helvetica-neue ${screeningType === ScreeningType.ONLINE ? 'text-gray-200/50' : ''}`}
                            onClick={increaseQuantity}
                            disabled={screeningType === ScreeningType.ONLINE}
                        >
                            +
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CartItem;
